import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import SFChip from "./index";
import useStyled from "../../hooks/useStyled";
import { SFClearIcon } from "../icons";
import { SFStack, SFGridContainer, SFGridItem } from "../grid";
import Typography from "@mui/material/Typography";

const { styled } = useStyled();

const StyledIcon = styled(SFClearIcon)(({ theme }) => ({
  margin: `${theme.spacing(0.5)} !important`,
  marginRight: "2px !important",
  padding: "2px !important",
  fontSize: "20px !important",
  borderRadius: "50%",
  color: `${theme.palette.grey300.main} !important`,
  backgroundColor: "rgba(255, 255, 255, 0.10)",
}));

const Wrapper = styled(SFStack)(({ theme }) => ({
  maxHeight: "200px",
  overflow: "auto",
  padding: theme.spacing(0.5),
  width: "100%",
}));

const SFFilterChips = ({
  label,
  items,
  size = "medium",
  variant = "primary",
  maxWidth,
  canDelete = false,
  onDelete,
  customActions = null,
  limit = 0,
  customChipActions = null,
}) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const [collapsedItems, setCollapsedItems] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (limit > 0 && !isExpanded) {
      setVisibleItems(items.slice(0, limit));
      if (items.length > limit)
        setCollapsedItems(items.slice(limit, items.length));
      else setCollapsedItems(null);
    } else setVisibleItems(items);
  }, [items, isExpanded]);

  return (
    <Wrapper>
      <SFGridContainer spacing={1.5}>
        {visibleItems.length > 0 &&
          visibleItems.map((item, index) => (
            <SFGridItem key={index}>
              <SFChip
                variant={variant}
                onDelete={() => onDelete(item)}
                size={size}
                sx={{
                  maxWidth: maxWidth,
                  color: "#F8F8FF",
                  padding: "4px",
                  borderColor: "#747480",
                }}
                label={
                  <div className="text-single-line-ellipsis w-100">
                    <Typography variant="chip">
                      {item?.label ? item.label : item}
                    </Typography>
                  </div>
                }
                deleteIcon={<StyledIcon />}
              />
            </SFGridItem>
          ))}
        {collapsedItems && (
          <SFGridItem key={"collapsed-items"}>
            <SFChip
              variant={variant}
              size={size}
              onClick={() => setIsExpanded((prevState) => !prevState)}
              sx={{
                maxWidth: maxWidth,
                color: "#F8F8FF",
                padding: "4px",
                borderColor: "#747480",
              }}
              label={
                <div className="text-single-line-ellipsis w-100">
                  <Typography variant="chip">
                    {isExpanded ? "Show less" : `+${collapsedItems.length}`}
                  </Typography>
                </div>
              }
            />
          </SFGridItem>
        )}
        {customActions && (
          <SFGridItem key={"customActions"}>{customActions}</SFGridItem>
        )}
        {customChipActions && (
          <SFGridItem key={"collapsed-items"}>
            <SFChip
              variant={variant}
              size={size}
              {...(customChipActions.onClick && { clickable: true })}
              {...(customChipActions.onClick && {
                onClick: () => customChipActions.onClick(),
              })}
              sx={{
                maxWidth: maxWidth,
                color: "#F8F8FF",
                padding: "4px",
                borderColor: "#747480",
              }}
              label={
                <div className="text-single-line-ellipsis w-100">
                  <Typography variant="chip">
                    {customChipActions.label}
                  </Typography>
                </div>
              }
            />
          </SFGridItem>
        )}
      </SFGridContainer>
    </Wrapper>
  );
};

SFFilterChips.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  size: PropTypes.oneOf(["medium", "small"]),
  canDelete: PropTypes.bool,
  variant: PropTypes.oneOf(["primary", "filterChip", "outlined"]),
  onDeleteAll: PropTypes.func,
  label: PropTypes.node,
};
export default SFFilterChips;
